import { MountainSnow, Heart, Users, CalendarRange, MessagesSquare, Route, ShieldCheck, BedDouble, Car, Binoculars, Coffee, Sailboat, Headset, ClipboardList, HandCoins, MapPin, Compass } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";

const WHY_CARDS = [
  { icon: MountainSnow, title: "Scenic Beauty", text: "Mountains, valleys, lakes and meadows." },
  { icon: Heart, title: "Romantic Escapes", text: "Perfect for couples and honeymoon holidays." },
  { icon: Users, title: "Family Adventures", text: "Comfortable experiences for families of all ages." },
  { icon: CalendarRange, title: "Year-Round Experiences", text: "Snow, summer landscapes, autumn colours and peaceful retreats." },
];

export const WhyKashmir = () => (
  <section className="py-16 sm:py-24" data-testid="why-kashmir-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="The Destination" title="Why Kashmir?"
        subtitle="Kashmir brings together snow-covered mountains, peaceful lakes, lush valleys, charming towns and unforgettable experiences — all in one destination." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY_CARDS.map(({ icon: Icon, title, text }, i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div className="h-full rounded-xl bg-white p-6 ring-1 ring-[#0F2F24]/8 shadow-sm hover:shadow-md transition-shadow" data-testid={`why-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-[#E3EAE4]">
                <Icon className="h-5 w-5 text-[#0F2F24]" />
              </span>
              <h3 className="font-serif text-lg font-semibold text-[#0F2F24]">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [
  { icon: MessagesSquare, step: "Step 1", title: "Tell Us Your Plans", text: "Share your dates, travellers and preferences." },
  { icon: Route, step: "Step 2", title: "Get Your Personalised Itinerary", text: "Our travel expert creates a Kashmir holiday around your requirements." },
  { icon: ShieldCheck, step: "Step 3", title: "Travel With Confidence", text: "Confirm your trip and enjoy your Kashmir holiday with dedicated assistance." },
];

export const HowItWorks = () => (
  <section className="py-16 sm:py-24 bg-[#E3EAE4]/60" data-testid="how-it-works-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="Simple Process" title="Your Kashmir Holiday in 3 Simple Steps" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-10">
        {STEPS.map(({ icon: Icon, step, title, text }, i) => (
          <Reveal key={step} delay={i * 0.12}>
            <div className="relative h-full rounded-xl bg-white p-8 ring-1 ring-[#0F2F24]/8 shadow-sm" data-testid={`how-step-${i + 1}`}>
              <span className="text-xs font-mono uppercase tracking-[0.2em] font-semibold text-[#C28519]">{step}</span>
              <span className="my-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#0F2F24]">
                <Icon className="h-6 w-6 text-[#D99B26]" />
              </span>
              <h3 className="font-serif text-xl font-semibold text-[#0F2F24]">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const INCLUSIONS = [
  { icon: BedDouble, label: "Hotel Stay" },
  { icon: Car, label: "Airport Transfers" },
  { icon: Car, label: "Private Transportation" },
  { icon: Binoculars, label: "Sightseeing" },
  { icon: Coffee, label: "Breakfast" },
  { icon: Sailboat, label: "Shikara Ride" },
  { icon: Headset, label: "Local Assistance" },
  { icon: ClipboardList, label: "Customised Itinerary" },
];

export const WhatsIncluded = () => (
  <section className="py-16 sm:py-24" data-testid="whats-included-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="Travel Comfortably" title="Everything You Need for a Comfortable Kashmir Holiday" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {INCLUSIONS.map(({ icon: Icon, label }) => (
          <div key={label} className="flex flex-col items-center gap-3 rounded-xl bg-white p-6 text-center ring-1 ring-[#0F2F24]/8 shadow-sm" data-testid={`inclusion-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E3EAE4]">
              <Icon className="h-5 w-5 text-[#0F2F24]" />
            </span>
            <span className="text-sm font-semibold text-[#1E2922]">{label}</span>
          </div>
        ))}
      </div>
      <p className="mt-6 text-center text-xs text-slate-500" data-testid="inclusions-disclaimer">
        Items are included as per your selected package — every quote clearly lists what is covered.
      </p>
    </div>
  </section>
);

const WHY_US = [
  { icon: MapPin, title: "Kashmir Specialists", text: "Focused expertise in Kashmir holidays." },
  { icon: ClipboardList, title: "Personalised Itineraries", text: "Trips designed around your dates and interests." },
  { icon: BedDouble, title: "Handpicked Stays", text: "Hotels selected based on location, comfort and value." },
  { icon: Car, title: "Reliable Transfers", text: "Comfortable transportation for your Kashmir journey." },
  { icon: HandCoins, title: "Transparent Pricing", text: "Clear package details without unnecessary surprises." },
  { icon: Headset, title: "Travel Assistance", text: "Support before and during your trip." },
];

export const WhyUs = () => (
  <section className="py-16 sm:py-24 bg-[#0F2F24]" data-testid="why-us-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="The Book Kashmir Difference" title="Why Travel With Book Kashmir?" light />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {WHY_US.map(({ icon: Icon, title, text }, i) => (
          <Reveal key={title} delay={i * 0.07}>
            <div className="h-full rounded-xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur-sm" data-testid={`why-us-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-[#D99B26]/15">
                <Icon className="h-5 w-5 text-[#D99B26]" />
              </span>
              <h3 className="font-serif text-lg font-semibold text-[#FAF7F2]">{title}</h3>
              <p className="mt-2 text-sm text-[#FAF7F2]/70 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
